import { ChangeDetectionStrategy, Component, HostListener } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, merge, Subject } from 'rxjs';
import {
    filter,
    map,
    mapTo,
    shareReplay,
    startWith,
    switchMap,
    tap,
    withLatestFrom,
} from 'rxjs/operators';
import { UsersActionTypes } from '../../../dave-data-module/State/actions/users.actions';
import { getPartnerOffices, getPartners } from '../../../dave-data-module/State/selectors/partners.selectors';
import { UserEntity } from '../../../dave-data-module/entities/user.entity';
import { getManagedUsers } from '../../../dave-data-module/State/selectors/users.selectors';
import { State } from '../../../dave-data-module/State';
import { userRequiredFieldValidator } from '../../../helper/validation.helper';
import { AppDialogService } from '../../../dave-utils-module/app-dialog-module/app-dialog.service';
import {
    CustomPropertyType,
    IProfileTemplateData,
} from '../../../dave-utils-module/dave-shared-components-module/components/detail-views/profile-template/profile-template.component';
import { isNotNullOrUndefined } from '../../../helper/helper';
import { ComponentCanDeactivate } from '../../../dave-data-module/guards/pending-changes.guard';

@Component({
    selector: 'app-detail-user',
    templateUrl: './detail-user.component.html',
    styleUrls: ['./detail-user.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DetailUserComponent implements ComponentCanDeactivate {
    public Data$: Observable<{
        editing: boolean;
        managedUser: UserEntity;
        profileData: IProfileTemplateData;
    }>;

    public ManagedUserForm = this.fb.group(
        {
            Firstname: [null],
            Lastname: [null],
            Email: [null, [Validators.email, Validators.required]],
        },
        { validators: userRequiredFieldValidator },
    ) as FormGroupTyped<{
        Firstname: string | null;
        Lastname: string | null;
        Email: string | null;
    }>;

    public OpenEdit: Subject<void> = new Subject<void>();
    public CloseEdit: Subject<void> = new Subject<void>();
    public Save: Subject<void> = new Subject<void>();
    public DeleteUser: Subject<void> = new Subject<void>();

    constructor(
        private fb: UntypedFormBuilder,
        appDialog: AppDialogService,
        route: ActivatedRoute,
        store: Store<State>,
    ) {
        /** (hot, behavior, NN) Der anzuzeigende Nutzer */
        const managedUser$ = route.paramMap.pipe(
            map(paramMap => +paramMap.get('userId')),
            switchMap(managedUserId =>
                store.select(getManagedUsers).pipe(
                    filter(isNotNullOrUndefined),
                    map(managedUsers =>
                        managedUsers.find(managedUser => managedUser.Id === managedUserId),
                    ),
                    tap(managedUser => !managedUser && console.error('Could not find UserEntity')),
                    filter(isNotNullOrUndefined),
                ),
            ),
            shareReplay({ refCount: true, bufferSize: 1 }),
        );

        /** (behavior, NN) Der Partner des anzuzeigenden Nutzers */
        const partnerMainOffice$ = managedUser$.pipe(
            switchMap(managedUser =>
                store.select(getPartnerOffices).pipe(
                    filter(isNotNullOrUndefined),
                    map(partnerOffices => partnerOffices.find(po => po.PartnerId === managedUser.PartnerId && po.MainOffice)),
                    tap(partner => !partner && console.error('Could not find PartnerEntity')),
                    filter(isNotNullOrUndefined),
                ),
            ),
        );

        /** (behavior, NN) Die Daten für die ProfileTemplateComponent */
        const profileData$ = combineLatest([partnerMainOffice$, managedUser$]).pipe(
            map(
                ([partner, managedUser]): IProfileTemplateData => ({
                    Icon: managedUser.Deleted ? 'user-slash' : 'user',
                    Properties: [
                        {
                            icon: 'trash',
                            options: { type: CustomPropertyType.Subtitle },
                            value: partner.Name,
                        },
                        {
                            icon: 'trash',
                            options: { type: CustomPropertyType.Title },
                            value: `${managedUser.DisplayName}${
                                managedUser.Deleted ? ' (gelöscht)' : ''
                            }`,
                        },
                        {
                            icon: 'trash',
                            formControl: this.ManagedUserForm.controls.Lastname as UntypedFormControl,
                            key: 'Nachname',
                        },
                        {
                            icon: 'trash',
                            formControl: this.ManagedUserForm.controls.Firstname as UntypedFormControl,
                            key: 'Vorname',
                        },
                        {
                            icon: 'envelope',
                            formControl: this.ManagedUserForm.controls.Email as UntypedFormControl,
                            key: 'E-Mail',
                            options: { type: CustomPropertyType.Mailto },
                            value: managedUser.Email,
                        },
                    ],
                }),
            ),
        );

        /** (behavior, NN) Ob die Bearbeitungsansicht aktiv ist */
        const editing$ = merge(
            this.OpenEdit.pipe(mapTo(true)),
            this.CloseEdit.pipe(mapTo(false)),
        ).pipe(startWith(false));

        /** (effect) Setzt die Werte des Formulars für die Bearbeitungsansicht */
        const openEdit$ = this.OpenEdit.pipe(
            withLatestFrom(managedUser$),
            tap(([, managedUser]) =>
                this.ManagedUserForm.reset({
                    Email: managedUser.Email,
                    Firstname: managedUser.Firstname,
                    Lastname: managedUser.Lastname,
                }),
            ),
        );

        /** (effect) Speichert die Änderungen am Nutzer */
        const save$ = this.Save.pipe(
            withLatestFrom(managedUser$),
            map(
                ([, managedUser]) =>
                    new UserEntity(
                        managedUser.Id,
                        null,
                        this.ManagedUserForm.value.Email,
                        null,
                        null,
                        null,
                        managedUser.PartnerId,
                        this.ManagedUserForm.value.Firstname,
                        this.ManagedUserForm.value.Lastname,
                    ),
            ),
            tap(newUser => {
                if (this.ManagedUserForm.valid) {
                    store.dispatch(UsersActionTypes.ModifyManageUser({Payload: newUser}));
                    this.CloseEdit.next();
                }
            }),
        );

        /** (effect) Öffnet einen Bestätigungsdialog und löscht ggf. den Nutzer */
        const deleteUser$ = this.DeleteUser.pipe(
            withLatestFrom(managedUser$),
            switchMap(([, managedUser]) =>
                appDialog
                    .OpenConfirmationDialog({
                        paragraph: `Benutzer ${managedUser.DisplayName} wirklich löschen?`,
                        styleDelete: true,
                    })
                    .pipe(
                        tap(
                            ([result,]) =>
                                result && store.dispatch(UsersActionTypes.DeleteManageUser({Payload: managedUser.Id})),
                        ),
                    ),
            ),
        );

        this.Data$ = combineLatest([
            editing$,
            managedUser$,
            profileData$,
            // Seiteneffekte - startWith(null) erzeugt deprecation-Warnung da kein null checking
            merge(openEdit$, save$, deleteUser$).pipe(startWith('')),
        ]).pipe(
            map(([editing, managedUser, profileData]) => ({
                editing,
                managedUser,
                profileData,
            })),
            shareReplay({ refCount: true, bufferSize: 1 }),
        );
    }
    @HostListener('window:beforeunload')
    // tslint:disable-next-line:naming-convention
    canDeactivate(): Observable<boolean> | boolean {
        return this.Data$.pipe(map(data => !data.editing || !this.ManagedUserForm.dirty));
    }
}
